import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Heading,
  HStack,
  VStack,
  Tooltip,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getWorkingHours,
  getUnavailableSlots,
  updateUnavailableSlots,
} from "../services/consultantService";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment-jalaali";
import api from "../services/api";

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const MONTH_NAMES = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"
];

function formatMinutesAsTime(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function dateToKey(d) {
  if (!d) return "";
  const x = new Date(d);
  const y = x.getFullYear();
  const m = (x.getMonth() + 1).toString().padStart(2, "0");
  const day = x.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isSlotInRange(slotStartMin, slotEndMin, startTime, endTime, breakStart, breakEnd) {
  const toMin = (t) => {
    const [h, m] = (t || "00:00").split(":").map(Number);
    return h * 60 + m;
  };
  const s = toMin(startTime);
  const e = toMin(endTime);
  if (slotStartMin < s || slotEndMin > e) return false;
  if (breakStart && breakEnd) {
    const bs = toMin(breakStart);
    const be = toMin(breakEnd);
    const overlapsBreak = slotStartMin < be && slotEndMin > bs;
    if (overlapsBreak) return false;
  }
  return true;
}

function timeStrToMinutes(time) {
  const [hours, minutes] = (time || "00:00").split(":").map(Number);
  return hours * 60 + minutes;
}

/** بازه‌های استراحت همان روز (اصلی + آرایه breaks) به دقیقه */
function getBreakIntervalsMinutes(daySchedule) {
  if (!daySchedule) return [];
  const breakStartMinutes = daySchedule.breakStart
    ? timeStrToMinutes(daySchedule.breakStart)
    : null;
  const breakEndMinutes = daySchedule.breakEnd
    ? timeStrToMinutes(daySchedule.breakEnd)
    : null;
  const breaksFromArray =
    Array.isArray(daySchedule.breaks) && daySchedule.breaks.length > 0
      ? daySchedule.breaks
          .filter((b) => b && b.startTime && b.endTime)
          .map((b) => ({
            start: timeStrToMinutes(b.startTime),
            end: timeStrToMinutes(b.endTime),
          }))
      : [];
  const mainBreak =
    breakStartMinutes != null && breakEndMinutes != null
      ? [{ start: breakStartMinutes, end: breakEndMinutes }]
      : [];
  return [...mainBreak, ...breaksFromArray];
}

/** همان منطق تولید اسلات در `/appointments/available-slots` */
function computeValidSlotStartsForDay(daySchedule, sessionDur) {
  if (!daySchedule?.startTime || !daySchedule?.endTime) return [];
  const startMinutes = timeStrToMinutes(daySchedule.startTime);
  const endMinutes = timeStrToMinutes(daySchedule.endTime);
  const dayBreaks = getBreakIntervalsMinutes(daySchedule);
  const workingStarts = [];
  let currentTime = startMinutes;

  while (currentTime + sessionDur <= endMinutes) {
    if (dayBreaks.length > 0) {
      const slotStart = currentTime;
      const slotEnd = currentTime + sessionDur;
      const overlappingBreaks = dayBreaks.filter(
        (b) => slotStart < b.end && slotEnd > b.start
      );
      if (overlappingBreaks.length > 0) {
        currentTime = Math.max(...overlappingBreaks.map((b) => b.end));
        continue;
      }
    }
    workingStarts.push(currentTime);
    currentTime += sessionDur;
  }

  return workingStarts;
}

function mergeSlotsAndBreaksForDisplay(slotStarts, sessionDur, breakIntervals) {
  const items = [];
  slotStarts.forEach((s) =>
    items.push({ kind: "slot", start: s, end: s + sessionDur })
  );
  breakIntervals.forEach((b) =>
    items.push({ kind: "break", start: b.start, end: b.end })
  );
  items.sort((a, b) => a.start - b.start || (a.kind === "break" ? -1 : 1));
  return items;
}

function getScheduleForMonthDay(workingHours, jYear, jMonth, dayNum) {
  const m = moment(`${jYear}/${jMonth}/${dayNum}`, "jYYYY/jM/jD");
  const dayName = DAY_NAMES[m.day()];
  return workingHours.find((wh) => wh.day === dayName);
}

function UnavailableHours() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const consultantId = searchParams.get("consultant");

  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const now = useMemo(() => moment(), []);
  const [jYear, setJYear] = useState(now.jYear());
  const [jMonth, setJMonth] = useState(now.jMonth() + 1);

  const queryClient = useQueryClient();

  const { data: workingHoursData, isLoading: loadingWh } = useQuery(
    ["workingHours", consultantId],
    () => getWorkingHours(consultantId),
    { enabled: !!consultantId }
  );

  const { data: unavailableData, isLoading: loadingUnav } = useQuery(
    ["unavailableSlots", consultantId],
    () => getUnavailableSlots(consultantId),
    { enabled: !!consultantId }
  );

  const updateMutation = useMutation(
    (payload) => updateUnavailableSlots(consultantId, payload),
    {
      onSuccess: () => {
        toast.success("ساعات غیرفعال ذخیره شد");
        queryClient.invalidateQueries(["unavailableSlots", consultantId]);
      },
      onError: (err) => {
        toast.error(err.message || "خطا در ذخیره");
      },
    }
  );

  const workingHours = workingHoursData?.data?.workingHours || [];
  const sessionDuration = Math.max(15, Number(workingHoursData?.data?.sessionDuration) || 60);
  const unavailableSlots = unavailableData?.data?.unavailableSlots || [];
  const consultantName = unavailableData?.data?.consultantName || workingHoursData?.data?.consultantName || "";

  const daysInMonth = moment.jDaysInMonth(jYear, jMonth - 1);
  const monthTitle = `${MONTH_NAMES[jMonth - 1]} ${jYear}`;

  // نوبت‌های رزروشده ماه جاری به تفکیک روز
  const [bookedByDate, setBookedByDate] = useState({}); // { 'YYYY-MM-DD': [{ time, duration, status, paymentStatus }, ...] }
  const lastFetchedBookedKeyRef = useRef("");

  useEffect(() => {
    if (!consultantId) return;
    const fetchKey = `${consultantId}-${jYear}-${jMonth}`;
    if (lastFetchedBookedKeyRef.current === fetchKey) return;

    let cancelled = false;
    (async () => {
      try {
        const startDate = moment(`${jYear}/${jMonth}/1`, "jYYYY/jM/jD").format("YYYY-MM-DD");
        const endDate = moment(`${jYear}/${jMonth}/${daysInMonth}`, "jYYYY/jM/jD").format("YYYY-MM-DD");

        const res = await api.post("/appointments/booked-slots-range", {
          consultantId,
          startDate,
          endDate,
        });

        if (cancelled) return;
        const byDate = res?.data?.data?.appointmentsByDate || {};
        const normalized = {};
        Object.keys(byDate).forEach((dateKey) => {
          normalized[dateKey] = (byDate[dateKey] || []).map((a) => ({
            time: a.time && a.time.length === 4 ? `0${a.time}` : a.time,
            duration: Number(a.duration) || 60,
            status: a.status || "scheduled",
            paymentStatus: a.paymentStatus || "pending",
          }));
        });
        setBookedByDate(normalized);
        lastFetchedBookedKeyRef.current = fetchKey;
      } catch (e) {
        // در صورت خطا، حالت قبلی حفظ می‌شود
      }
    })();
    return () => { cancelled = true; };
  }, [consultantId, jYear, jMonth, daysInMonth]);

  const findAppointmentForSlot = (dateKey, slotStartMin, slotEndMin) => {
    const apps = bookedByDate[dateKey] || [];
    return apps.find((a) => {
      if (!a.time) return false;
      const [h, mm] = a.time.split(":").map(Number);
      const aStart = (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(mm) ? mm : 0);
      const aEnd = aStart + (Number(a.duration) || 60);
      return aStart < slotEndMin && aEnd > slotStartMin;
    });
  };

  const goPrev = () => {
    if (jMonth === 1) {
      setJMonth(12);
      setJYear((y) => y - 1);
    } else {
      setJMonth((m) => m - 1);
    }
  };

  const goNext = () => {
    if (jMonth === 12) {
      setJMonth(1);
      setJYear((y) => y + 1);
    } else {
      setJMonth((m) => m + 1);
    }
  };

  const isSlotUnavailable = (dateKey, startTime, endTime) => {
    return unavailableSlots.some(
      (s) => dateToKey(s.date) === dateKey && s.startTime === startTime && s.endTime === endTime
    );
  };

  const toggleSlot = (day, slotStartMin) => {
    const startTime = formatMinutesAsTime(slotStartMin);
    const endTime = formatMinutesAsTime(slotStartMin + sessionDuration);
    const m = moment(`${jYear}/${jMonth}/${day}`, "jYYYY/jM/jD");
    const dateKey = m.format("YYYY-MM-DD");

    const exists = isSlotUnavailable(dateKey, startTime, endTime);
    let newList;
    if (exists) {
      newList = unavailableSlots.filter(
        (s) =>
          !(dateToKey(s.date) === dateKey && s.startTime === startTime && s.endTime === endTime)
      );
    } else {
      newList = [
        ...unavailableSlots,
        { date: dateKey, startTime, endTime },
      ];
    }
    updateMutation.mutate(newList);
  };

  const getPaymentStatusLabel = (paymentStatus) => {
    if (paymentStatus === "paid") return "پرداخت: کامل";
    if (paymentStatus === "partial") return "پرداخت: جزئی";
    return "پرداخت: در انتظار";
  };

  const getCellStatus = (day, slotStartMin) => {
    const m = moment(`${jYear}/${jMonth}/${day}`, "jYYYY/jM/jD");
    const dayOfWeek = m.day();
    const dayName = DAY_NAMES[dayOfWeek];
    const schedule = workingHours.find((wh) => wh.day === dayName);
    if (!schedule || !schedule.isWorking)
      return "off";
    const slotEndMin = slotStartMin + sessionDuration;
    const inRange = isSlotInRange(
      slotStartMin,
      slotEndMin,
      schedule.startTime,
      schedule.endTime,
      schedule.breakStart,
      schedule.breakEnd
    );
    if (!inRange) return "off";
    const dateKey = m.format("YYYY-MM-DD");
    const appointment = findAppointmentForSlot(dateKey, slotStartMin, slotEndMin);
    if (appointment) {
      if (appointment.status === "pending") {
        return {
          status: "pendingAppointment",
          paymentLabel: getPaymentStatusLabel(appointment.paymentStatus),
        };
      }
      return {
        status: "confirmedAppointment",
        paymentLabel: getPaymentStatusLabel(appointment.paymentStatus),
      };
    }
    if (isSlotUnavailable(dateKey, formatMinutesAsTime(slotStartMin), formatMinutesAsTime(slotEndMin))) {
      return { status: "inactive" };
    }
    return { status: "active" };
  };

  if (!consultantId) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={6} minH="100vh">
          <Alert status="warning">
            <AlertIcon />
            مشاور انتخاب نشده است. از لیست مشاوران روی «ساعات غیرفعال» کلیک کنید.
          </Alert>
          <Button mt={4} onClick={() => navigate("/consultants")}>
            بازگشت به مشاوران
          </Button>
        </Box>
      </Flex>
    );
  }

  if (!isAdmin()) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={6} minH="100vh">
          <Alert status="error">
            <AlertIcon />
            فقط مدیر و مدیرکل می‌توانند ساعات غیرفعال را تنظیم کنند.
          </Alert>
          <Button mt={4} onClick={() => navigate("/consultants")}>
            بازگشت به مشاوران
          </Button>
        </Box>
      </Flex>
    );
  }

  const isLoading = loadingWh || loadingUnav;

  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={6} minH="100vh">
          <Flex justify="center" align="center" minH="300px">
            <Spinner size="xl" color="teal.500" />
            <Text mr={4}>در حال بارگذاری...</Text>
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
      <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
        <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
          <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
            <Box>
              <Heading size="md" color={textColor}>
                ساعات غیرفعال
                {consultantName && (
                  <Text as="span" fontSize="lg" color="gray.500" fontWeight="normal" mr={2}>
                    – {consultantName}
                  </Text>
                )}
              </Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                هر روز فقط بازه‌های همان روز نمایش داده می‌شود؛ شروع و پایان روی هر مربع نوشته شده است. فقط آبی و مشکی قابل تغییر هستند.
              </Text>
            </Box>
            <HStack>
              <Button size="sm" leftIcon={<ChevronRightIcon />} onClick={goPrev}>
                ماه قبل
              </Button>
              <Text fontWeight="bold" minW="140px" textAlign="center">
                {monthTitle}
              </Text>
              <Button size="sm" rightIcon={<ChevronLeftIcon />} onClick={goNext}>
                ماه بعد
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate("/consultants")}>
                بازگشت به مشاوران
              </Button>
            </HStack>
          </Flex>

          <Wrap mb={4} spacing={4}>
            <WrapItem>
              <HStack>
                <Box w="24px" h="24px" borderRadius="md" bg="gray.200" />
                <Text fontSize="sm">خارج از ساعات کاری</Text>
              </HStack>
            </WrapItem>
            <WrapItem>
              <HStack>
                <Box
                  w="24px"
                  h="24px"
                  borderRadius="md"
                  bg="gray.300"
                  borderWidth="1px"
                  borderColor="gray.400"
                />
                <Text fontSize="sm">استراحت</Text>
              </HStack>
            </WrapItem>
            <WrapItem>
              <HStack>
                <Box w="24px" h="24px" borderRadius="md" bg="blue.500" />
                <Text fontSize="sm">فعال</Text>
              </HStack>
            </WrapItem>
            <WrapItem>
              <HStack>
                <Box w="24px" h="24px" borderRadius="md" bg="black" />
                <Text fontSize="sm">غیرفعال</Text>
              </HStack>
            </WrapItem>
            <WrapItem>
              <HStack>
                <Box w="24px" h="24px" borderRadius="md" bg="yellow.400" />
                <Text fontSize="sm">در انتظار ملاقات</Text>
              </HStack>
            </WrapItem>
            <WrapItem>
              <HStack>
                <Box w="24px" h="24px" borderRadius="md" bg="green.500" />
                <Text fontSize="sm">تایید شده ملاقات</Text>
              </HStack>
            </WrapItem>
          </Wrap>

          <VStack
            align="stretch"
            spacing={0}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            overflow="hidden"
          >
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const m = moment(`${jYear}/${jMonth}/${day}`, "jYYYY/jM/jD");
              const dayOfWeek = m.day();
              const dayNameFa = ["یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنج‌شنبه", "جمعه", "شنبه"][dayOfWeek];
              const schedule = getScheduleForMonthDay(workingHours, jYear, jMonth, day);
              const isWorkingDay = !!(schedule?.isWorking);
              const daySlots = isWorkingDay
                ? computeValidSlotStartsForDay(schedule, sessionDuration)
                : [];
              const breakIntervals = isWorkingDay ? getBreakIntervalsMinutes(schedule) : [];
              const timelineItems = isWorkingDay
                ? mergeSlotsAndBreaksForDisplay(daySlots, sessionDuration, breakIntervals)
                : [];
              return (
                <Flex
                  key={day}
                  direction={{ base: "column", md: "row" }}
                  gap={3}
                  align={{ base: "stretch", md: "flex-start" }}
                  p={3}
                  bg={bgCard}
                  borderBottomWidth="1px"
                  borderColor={borderColor}
                  _last={{ borderBottomWidth: 0 }}
                >
                  <Box minW={{ md: "200px" }} flexShrink={0}>
                    <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                      {day} {MONTH_NAMES[jMonth - 1]}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {dayNameFa}
                    </Text>
                  </Box>
                  {!isWorkingDay ? (
                    <Text fontSize="sm" color="gray.500" py={1}>
                      در این روز ساعت کاری ثبت نشده است
                    </Text>
                  ) : timelineItems.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" py={1}>
                      بازهٔ نوبتی برای این روز تعریف نشده است
                    </Text>
                  ) : (
                    <Wrap spacing={2} flex={1}>
                      {timelineItems.map((item) => {
                        if (item.kind === "break") {
                          const br = `${formatMinutesAsTime(item.start)} – ${formatMinutesAsTime(item.end)}`;
                          return (
                            <WrapItem key={`${day}-break-${item.start}-${item.end}`}>
                              <Tooltip label="استراحت">
                                <Box
                                  minW="148px"
                                  maxW="190px"
                                  py={3}
                                  px={3}
                                  bg="gray.300"
                                  color="gray.700"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor="gray.400"
                                  textAlign="center"
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <Text fontWeight="bold" fontSize="xs">
                                    استراحت
                                  </Text>
                                  <Text fontSize="xs" mt={1} fontWeight="medium">
                                    {br}
                                  </Text>
                                </Box>
                              </Tooltip>
                            </WrapItem>
                          );
                        }

                        const slotStartMin = item.start;
                        const slotEndMin = item.end;
                        const timeRange = `${formatMinutesAsTime(slotStartMin)} – ${formatMinutesAsTime(slotEndMin)}`;
                        const cell = getCellStatus(day, slotStartMin);
                        const status = cell.status;
                        const isClickable = status === "active" || status === "inactive";
                        const bg =
                          status === "inactive"
                            ? "black"
                            : status === "pendingAppointment"
                              ? "yellow.400"
                              : status === "confirmedAppointment"
                                ? "green.500"
                                : status === "active"
                                  ? "blue.500"
                                  : "gray.200";
                        const cellLabel =
                          status === "pendingAppointment" || status === "confirmedAppointment"
                            ? cell.paymentLabel
                            : "";
                        return (
                          <WrapItem key={`${day}-${slotStartMin}`}>
                            <Tooltip
                              label={
                                status === "off"
                                  ? "خارج از ساعات کاری"
                                  : status === "pendingAppointment"
                                    ? `${timeRange} — در انتظار ملاقات`
                                    : status === "confirmedAppointment"
                                      ? `${timeRange} — تایید شده ملاقات`
                                      : status === "inactive"
                                        ? `${timeRange} — غیرفعال؛ کلیک برای فعال‌سازی`
                                        : `${timeRange} — فعال؛ کلیک برای غیرفعال‌سازی`
                              }
                            >
                              <Box
                                as="button"
                                type="button"
                                minW="148px"
                                maxW="190px"
                                py={3}
                                px={3}
                                bg={bg}
                                borderRadius="md"
                                color={status === "off" ? "gray.700" : "white"}
                                fontSize="xs"
                                lineHeight="1.35"
                                textAlign="center"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                _hover={isClickable ? { opacity: 0.88 } : {}}
                                cursor={isClickable ? "pointer" : "default"}
                                disabled={!isClickable || updateMutation.isLoading}
                                onClick={() => isClickable && toggleSlot(day, slotStartMin)}
                                transition="opacity 0.15s"
                              >
                                <Text fontWeight="bold" fontSize="sm" noOfLines={2}>
                                  {timeRange}
                                </Text>
                                {cellLabel ? (
                                  <Text fontSize="xs" mt={1.5} opacity={0.95} noOfLines={2}>
                                    {cellLabel}
                                  </Text>
                                ) : null}
                              </Box>
                            </Tooltip>
                          </WrapItem>
                        );
                      })}
                    </Wrap>
                  )}
                </Flex>
              );
            })}
          </VStack>
        </Box>
      </Box>
    </Flex>
  );
}

export default UnavailableHours;
